/**
 * The installable web app (hooshpazPwa) at app.hooshpaz.ir. Most visitors arrive from a search
 * engine on a phone and never install a store app, so every "get the app" spot also offers the web
 * app, and a recipe page opens the same recipe there (`/book/<slug>`: cook mode with timers,
 * «مال خودت کن», comments). `?open=adapt|comment` asks the web app to start that action on arrival.
 *
 * Every link is registered in WEB_APP_LINKS with its own GA4 event (landing property) and UTM tags:
 * utm_source=hooshpaz.ir, utm_medium=<where on the site>, utm_campaign=web_app, utm_content=<link id>.
 * The web app has no signup gift (roadmap Phase 57 decision), so copy pointing at it must not
 * promise free credits.
 */
export const WEB_APP_ORIGIN = 'https://app.hooshpaz.ir';

export const WEB_APP_LINKS = Object.freeze({
    navbar: { event: 'webapp_click_navbar', medium: 'navbar' },
    mobile_menu: { event: 'webapp_click_mobile_menu', medium: 'mobile_menu' },
    home_hero: { event: 'webapp_click_home_hero', medium: 'home_hero' },
    // The iPhone button in the download section (the iOS app was retired on 2026-11-03).
    download_iphone: { event: 'webapp_click_download_iphone', medium: 'download_section' },
    footer: { event: 'webapp_click_footer', medium: 'footer' },
    recipe_card: { event: 'webapp_click_recipe_card', medium: 'recipe_page', recipe: true },
    recipe_modify: { event: 'webapp_click_recipe_modify', medium: 'recipe_page', recipe: true, action: 'adapt' },
    recipe_promo: { event: 'webapp_click_recipe_promo', medium: 'recipe_page', recipe: true, action: 'adapt' },
    recipe_comment: { event: 'webapp_click_recipe_comment', medium: 'recipe_comments', recipe: true, action: 'comment' },
    recipe_more_replies: { event: 'webapp_click_recipe_more_replies', medium: 'recipe_comments', recipe: true },
    recipe_sticky_bar: { event: 'webapp_click_recipe_sticky_bar', medium: 'recipe_sticky_bar' },
});

const SLUG_PATTERN = /^[\p{L}\p{M}\p{N}‌_-]+$/u;

const linkFor = (id) => {
    const link = WEB_APP_LINKS[id];
    if (!link) throw new Error(`Unknown web app link: ${id}`);
    return link;
};

/**
 * The URL a registered link opens. Recipe links open `/book/<slug>`; an unexpected slug falls back
 * to the book list, never another path or host.
 */
export const webAppHref = (id, slug) => {
    const link = linkFor(id);
    let path = '/';
    if (link.recipe) {
        path = typeof slug === 'string' && SLUG_PATTERN.test(slug) ? `/book/${encodeURIComponent(slug)}` : '/book';
    }
    const url = new URL(path, WEB_APP_ORIGIN);
    if (link.action && path !== '/book') url.searchParams.set('open', link.action);
    url.searchParams.set('utm_source', 'hooshpaz.ir');
    url.searchParams.set('utm_medium', link.medium);
    url.searchParams.set('utm_campaign', 'web_app');
    url.searchParams.set('utm_content', id);
    return url.toString();
};

/** GA4 event on the landing property; a no-op when gtag is blocked or during SSR. */
export const trackWebAppClick = (id, slug) => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    const link = linkFor(id);
    window.gtag('event', link.event, {
        link_id: id,
        link_medium: link.medium,
        ...(link.action ? { link_action: link.action } : {}),
        ...(link.recipe && slug ? { recipe_slug: slug } : {}),
        page_path: window.location?.pathname,
    });
};
